import { useEffect } from "react";
import { App as AntApp, ConfigProvider, theme as antTheme } from "antd";
import { useDispatch, useSelector } from "react-redux";

import AppRoutes from "./routes";
import { restoreSession, selectAuthStatus, sessionExpired } from "./app/authSlice";
import { loadOrganization } from "./app/unitSlice";
import { selectTheme } from "./app/uiSlice";

/**
 * Ant Design reads the same palette the CSS custom properties define, so
 * components and bespoke markup stay in step across both themes.
 *
 * Sizing follows the BMR_Frontend design system: 36px controls (44 large,
 * 28 small), 14px base type, 6px radii — so the two products feel like one
 * suite rather than two apps that happen to share a colour.
 */
function antdTheme(mode) {
  const dark = mode === "dark";
  return {
    algorithm: dark ? antTheme.darkAlgorithm : antTheme.defaultAlgorithm,
    token: {
      // These MUST stay in step with styles/tokens.css — antd cannot read a
      // CSS custom property, so the palette is unavoidably written twice and
      // a change in one place is only half a change.
      colorPrimary: dark ? "#63a4f5" : "#1666d0",
      colorSuccess: dark ? "#4ecb85" : "#12894a",
      colorWarning: dark ? "#f5b342" : "#e07c00",
      colorError: dark ? "#ff8a7a" : "#d92020",
      colorInfo: dark ? "#63a4f5" : "#1666d0",
      // Antd's own greys are a step lighter than the tokens, so a page mixing
      // antd Text with our own markup showed two different "secondary" greys.
      colorText: dark ? "#f8fafc" : "#0b1220",
      colorTextSecondary: dark ? "#dbe3ee" : "#334155",
      colorTextTertiary: dark ? "#a3b1c4" : "#526077",
      colorTextDescription: dark ? "#a3b1c4" : "#526077",
      borderRadius: 6,
      borderRadiusLG: 8,
      borderRadiusSM: 4,
      fontSize: 14,
      fontSizeSM: 12,
      fontSizeLG: 16,
      controlHeight: 36,
      controlHeightLG: 44,
      controlHeightSM: 28,
    },
    components: {
      Button: {
        controlHeight: 36,
        controlHeightLG: 44,
        controlHeightSM: 28,
        fontWeight: 500,
        borderRadius: 6,
        // Flat, never gradient — the reference system is explicit about this.
        primaryShadow: "none",
        defaultShadow: "none",
      },
      Input: { controlHeight: 36, controlHeightLG: 44, controlHeightSM: 28, borderRadius: 6 },
      InputNumber: { controlHeight: 36, controlHeightLG: 44, controlHeightSM: 28, borderRadius: 6 },
      Select: { controlHeight: 36, controlHeightLG: 44, controlHeightSM: 28, borderRadius: 6 },
      DatePicker: { controlHeight: 36, controlHeightLG: 44, controlHeightSM: 28, borderRadius: 6 },
      Card: { borderRadiusLG: 8 },
      Layout: {
        siderBg: dark ? "#0b1220" : "#082440",
        headerBg: dark ? "#1e293b" : "#ffffff",
        headerHeight: 64,
      },
      Menu: {
        darkItemBg: "transparent",
        darkSubMenuItemBg: "transparent",
        // The selected row's fill and its left accent bar are painted
        // per-item in AppLayout, in that page's own colour. Antd's own
        // selected background would sit on top of them, so it is cleared
        // here; the radius goes with it so the bar meets the row edge.
        // Both variants are cleared: the drawer is dark in either theme, so
        // in light mode antd would otherwise fill the selected row with its
        // pale light-theme colour and it would read as a white block.
        darkItemSelectedBg: "transparent",
        itemSelectedBg: "transparent",
        itemBg: "transparent",
        subMenuItemBg: "transparent",
        // The drawer is dark in both themes, so its rows are lettered white
        // rather than following the light theme's near-black body text.
        itemColor: "#ffffff",
        darkItemColor: "#ffffff",
        itemHoverColor: "#ffffff",
        darkItemHoverColor: "#ffffff",
        groupTitleColor: "rgba(255,255,255,0.5)",
        darkGroupTitleColor: "rgba(255,255,255,0.5)",
        itemBorderRadius: 0,
        itemMarginInline: 0,
        // 38 not 42, and the inter-item margin collapsed: 15 nav rows plus 4
        // group headings have to fit an 900px-tall viewport without the
        // sider scrolling. At 42 + default margins it came to ~924px and
        // clipped the last entry.
        itemHeight: 38,
        itemMarginBlock: 0,
        groupTitleLineHeight: "28px",
      },
    },
  };
}

export default function App() {
  const dispatch = useDispatch();
  const mode = useSelector(selectTheme);
  const authStatus = useSelector(selectAuthStatus);

  useEffect(() => {
    dispatch(restoreSession());
  }, [dispatch]);

  // The api client announces a hard 401 (refresh exhausted) with this event;
  // flipping the auth state routes the user to /login instead of leaving
  // every panel stuck on an unexplained error.
  useEffect(() => {
    const expire = () => dispatch(sessionExpired());
    window.addEventListener("ebmr:session-expired", expire);
    return () => window.removeEventListener("ebmr:session-expired", expire);
  }, [dispatch]);

  // The plant hierarchy is behind auth, so it waits for a session.
  useEffect(() => {
    if (authStatus === "authenticated") dispatch(loadOrganization());
  }, [authStatus, dispatch]);

  return (
    <ConfigProvider theme={antdTheme(mode)}>
      <AntApp>
        <AppRoutes />
      </AntApp>
    </ConfigProvider>
  );
}
